import { useAccount } from "#contexts/AccountContext"
import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import { queryKeys } from "#lib/query-keys"
import { showToast } from "#lib/toast"
import { homeFinancialSDK } from "#sdk/sdk"
import type {
  CreateExpenseCategory,
  UpdateExpenseCategory,
} from "@home-finance-manager/shared/types/expense_categories"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

export function useCategories() {
  const { currentAccount } = useAccount()
  return useQuery({
    queryKey: queryKeys.categories.lists(),
    queryFn: () => homeFinancialSDK.accounts.expenseCategories.getCategories(),
    enabled: !!currentAccount,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useCategory(id: number) {
  const { currentAccount } = useAccount()
  return useQuery({
    queryKey: queryKeys.categories.detail(id),
    queryFn: () => homeFinancialSDK.accounts.expenseCategories.getCategory(id),
    enabled: !!id && !!currentAccount,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useCreateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateExpenseCategory) =>
      homeFinancialSDK.accounts.expenseCategories.createCategory(data),
    onMutate: async (newCategory) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.categories.all })
      const previousCategories = queryClient.getQueryData(queryKeys.categories.lists())

      queryClient.setQueryData(queryKeys.categories.lists(), (old: any[] | undefined) => {
        if (!old) return old
        const optimisticCategory = {
          ...newCategory,
          id: Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as any
        return [...old, optimisticCategory]
      })

      return { previousCategories }
    },
    onError: (_, __, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(queryKeys.categories.lists(), context.previousCategories)
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all })
      // Invalidate dashboard to reflect the new category
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      showToast.success(`Category "${variables.name}" created successfully`)
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useUpdateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateExpenseCategory }) =>
      homeFinancialSDK.accounts.expenseCategories.updateCategory(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.categories.all })
      const previousCategories = queryClient.getQueryData(queryKeys.categories.lists())
      const previousCategory = queryClient.getQueryData(queryKeys.categories.detail(id))

      queryClient.setQueryData(queryKeys.categories.lists(), (old: any[] | undefined) => {
        if (!old) return old
        return old.map((category) =>
          category.id === id
            ? { ...category, ...data, updatedAt: new Date().toISOString() }
            : category
        )
      })

      queryClient.setQueryData(queryKeys.categories.detail(id), (old: any) => {
        if (!old) return old
        return { ...old, ...data, updatedAt: new Date().toISOString() }
      })

      return { previousCategories, previousCategory }
    },
    onError: (_, variables, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(queryKeys.categories.lists(), context.previousCategories)
      }
      if (context?.previousCategory) {
        queryClient.setQueryData(
          queryKeys.categories.detail(variables.id),
          context.previousCategory
        )
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.detail(variables.id) })
      // Invalidate dashboard to reflect the updated category
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      showToast.success("Category updated successfully")
    },
  })
}

export function useDeleteCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => homeFinancialSDK.accounts.expenseCategories.deleteCategory(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.categories.all })
      const previousCategories = queryClient.getQueryData(queryKeys.categories.lists())

      queryClient.setQueryData(queryKeys.categories.lists(), (old: any[] | undefined) => {
        if (!old) return old
        return old.filter((category) => category.id !== id)
      })

      return { previousCategories }
    },
    onError: (_, __, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(queryKeys.categories.lists(), context.previousCategories)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all })
      // Invalidate dashboard to reflect the deleted category
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      showToast.success("Category deleted successfully")
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}
